import toast from 'react-hot-toast'

interface ApiError {
  response?: {
    status: number
    data?: {
      message?: string
      errors?: string[] | Record<string, string[]>
    }
  }
  code?: string
  message?: string
}

/**
 * Extract a user-friendly error message from an API error
 */
const getErrorMessage = (error: ApiError, operation = 'operation'): string => {
  // Handle 422 validation errors
  if (error.response?.status === 422) {
    // Check for Rails API error format: { success: false, error: { message: "..." } }
    const apiError = (error.response?.data as any)?.error
    if (apiError?.message) {
      return apiError.message
    }
    
    // Check for direct message in data
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    
    // Check for validation errors array/object
    const validationErrors = error.response?.data?.errors
    if (validationErrors && Array.isArray(validationErrors)) {
      return validationErrors.join(', ')
    }
    if (validationErrors && typeof validationErrors === 'object') {
      const errorMessages = Object.values(validationErrors).flat().join(', ')
      return errorMessages || `Invalid data provided for ${operation}`
    }
    
    return `Unable to complete ${operation} due to validation errors`
  }
  
  // Handle other specific status codes
  if (error.response?.status === 401) {
    return 'You need to log in to perform this action'
  }
  
  if (error.response?.status === 403) {
    return `You don't have permission to perform this ${operation}`
  }
  
  if (error.response?.status === 404) {
    return 'The requested resource was not found. It may have been deleted.'
  }
  
  if (error.response?.status === 409) {
    return `Cannot complete ${operation} - there may be a conflict with the current state`
  }
  
  if (error.response?.status === 429) {
    return 'Too many requests. Please wait a moment before trying again.'
  }
  
  if ((error.response?.status ?? 0) >= 500) {
    return 'Server error occurred. Please try again in a few moments.'
  }
  
  // Network errors
  if (error.code === 'NETWORK_ERROR' || !error.response) {
    return 'Network connection issue. Please check your internet connection.'
  }
  
  // Timeout errors
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.'
  }
  
  // Fallback to API message or generic message
  return error.response?.data?.message || error.message || `Failed to complete ${operation}. Please try again.`
}

/**
 * Show a success toast
 */
export const showSuccessToast = (message: string): void => {
  toast.success(message)
}

/**
 * Handle API errors consistently across the app
 */
export const handleApiError = (error: ApiError, operation = 'operation', showToast = true): string => {
  const errorMessage = getErrorMessage(error, operation)

  if (showToast) {
    toast.error(errorMessage, {
      duration: 5000, // Make toast last longer to ensure it's visible
      position: 'top-center', // Make it more prominent
    })
  }
  
  return errorMessage
}

